import type { Identified, TenantOwned, VersionedEntity, SoftDeletable } from "../../shared/kernel/entity.js";
import type { CompanyId } from "../../shared/kernel/identifiers.js";

export enum CompanyStatus {
  Active = "ACTIVE",
  Inactive = "INACTIVE"
}

export interface Company extends Identified<CompanyId>, TenantOwned, VersionedEntity, SoftDeletable {
  readonly legalName: string;
  readonly tradeName: string;
  readonly cnpj: string; // Formatted CNPJ (e.g. 00.000.000/0000-00)
  readonly normalizedCnpj: string; // Only digits (e.g. 00000000000000)
  readonly status: CompanyStatus;
}

export function normalizeCnpj(cnpj: string): string {
  return cnpj.replace(/\D/g, "");
}

export function formatCnpj(cnpj: string): string {
  const digits = normalizeCnpj(cnpj);
  if (digits.length !== 14) return cnpj;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
}

export function isValidCnpj(cnpj: string): boolean {
  if (!cnpj || typeof cnpj !== "string") return false;
  const digits = normalizeCnpj(cnpj);
  if (digits.length !== 14) return false;

  // Rejeita sequências com todos os dígitos repetidos (ex: 000..., 111...)
  if (/^(\d)\1{13}$/.test(digits)) return false;

  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum1 = 0;
  for (let i = 0; i < 12; i++) {
    sum1 += Number.parseInt(digits[i]!, 10) * weights1[i]!;
  }
  const rem1 = sum1 % 11;
  const dv1 = rem1 < 2 ? 0 : 11 - rem1;
  if (Number.parseInt(digits[12]!, 10) !== dv1) return false;

  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum2 = 0;
  for (let i = 0; i < 13; i++) {
    sum2 += Number.parseInt(digits[i]!, 10) * weights2[i]!;
  }
  const rem2 = sum2 % 11;
  const dv2 = rem2 < 2 ? 0 : 11 - rem2;
  if (Number.parseInt(digits[13]!, 10) !== dv2) return false;

  return true;
}

export function isValidCnpjFormat(cnpj: string): boolean {
  return isValidCnpj(cnpj);
}

export function maskCnpj(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 14);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
}

